/**
 * `useCatalogSample` — fetch up to N catalog products for a site, for
 * the readiness FixDrawer's structured-data branch. When the merchant
 * has synced catalog products the drawer uses them to generate ready-
 * to-paste Product JSON-LD with real values (name, SKU, image,
 * description, URL, price, currency, availability). When nothing is
 * available the drawer falls back to the placeholder template.
 *
 * The hook is a progressive enhancement: it returns `null` when there
 * is no HttpClient in context (tests, early scaffolds) and `[]` when
 * the site has no catalog or the fetch failed. No throw path.
 */

import { useEffect, useState } from 'react';

import { getCatalog } from '../../api/catalog.js';
import { useOptionalHttp } from '../../api/http-context.js';
import type { CatalogProduct } from '@sill/types';

/**
 * Fields the structured-data builder needs. Pulled out of the full
 * `CatalogProduct` wire shape so the drawer and the pure builder in
 * `@sill/types/readiness-fixes` agree on a minimal contract.
 */
export interface CatalogSampleProduct {
  readonly catalog_product_id: string;
  readonly title: string;
  readonly sku?: string;
  readonly description?: string;
  readonly url?: string;
  readonly image_url?: string;
  readonly price_minor?: number;
  readonly currency?: string;
  readonly availability?: string;
}

function toSample(p: CatalogProduct): CatalogSampleProduct {
  return {
    catalog_product_id: p.catalog_product_id,
    title: p.title,
    ...(p.sku !== undefined ? { sku: p.sku } : {}),
    ...(p.description !== undefined ? { description: p.description } : {}),
    ...(p.url !== undefined ? { url: p.url } : {}),
    ...(p.image_url !== undefined ? { image_url: p.image_url } : {}),
    ...(p.price_minor !== undefined ? { price_minor: p.price_minor } : {}),
    ...(p.currency !== undefined ? { currency: p.currency } : {}),
    ...(p.availability !== undefined ? { availability: p.availability } : {}),
  };
}

export interface CatalogSampleState {
  readonly loading: boolean;
  readonly products: readonly CatalogSampleProduct[];
}

/**
 * Fetch a small sample of catalog products for `site_id`. `enabled`
 * gates the fetch — the drawer only opens the hook when the user
 * actually navigates to the structured-data step, so we never read
 * the catalog on every render.
 */
export function useCatalogSample(
  site_id: string | null | undefined,
  enabled: boolean,
  limit: number = 6,
): CatalogSampleState {
  const http = useOptionalHttp();
  const [state, setState] = useState<CatalogSampleState>({ loading: false, products: [] });

  useEffect(() => {
    if (!enabled) return;
    if (http === null) return;
    if (site_id === null || site_id === undefined || site_id.length === 0) return;
    let cancelled = false;
    setState({ loading: true, products: [] });
    void getCatalog(http, site_id, { limit }).then(
      (res) => {
        if (cancelled) return;
        if (res.kind === 'ok') {
          setState({ loading: false, products: res.page.products.map(toSample) });
        } else {
          // Not an error we need to surface — the drawer falls back to
          // the placeholder template when there is no sample.
          setState({ loading: false, products: [] });
        }
      },
      () => {
        if (cancelled) return;
        setState({ loading: false, products: [] });
      },
    );
    return () => { cancelled = true; };
  }, [enabled, http, site_id, limit]);

  return state;
}
